import React from "react";
import Typography from "./Typography.jsx";
import { COLORS } from "../../constants";

const BADGE_VARIANTS = {
  earlyAccess: {
    backgroundColor: "#000000", // True black instead of PRIMARY_BLACK
    color: COLORS.BRAND.WHITE,
    icon: "🌟", // Star emoji for premium/special feeling
    text: "Early Access",
    type: "compact", // Uses compact Early Access styling
  },
  ffsLogo: {
    backgroundColor: COLORS.BRAND.PRIMARY_BLACK,
    color: COLORS.BRAND.WHITE,
    icon: "⚽", // Football emoji for logo
    text: "FootyFlowStates",
    type: "logo", // Uses logo-specific styling
  },
  success: {
    backgroundColor: COLORS.SUCCESS.PRIMARY,
    color: COLORS.BRAND.WHITE,
    icon: "✓",
    text: "Joined",
    type: "compact",
  },
  refer: {
    backgroundColor: COLORS.BRAND.PRIMARY_BLACK,
    color: COLORS.BRAND.WHITE,
    icon: "💎",
    text: "Refer a Friend",
    type: "compact",
  },
};

const Badge = ({
  variant = "earlyAccess",
  size = "desktop",
  className = "",
  style = {},
  onClick,
  customText,
  customIcon,
  ...props
}) => {
  const isMobile = size === "mobile" || size === "mobileSm";
  const badgeConfig = BADGE_VARIANTS[variant];

  const getDesktopTabletStyles = () => {
    const isLogo = badgeConfig.type === "logo";

    if (isLogo) {
      return {
        display: "flex",
        alignItems: "center",
        justifyContent: "center", // Center content within the badge
        gap: "0.25rem", // Gap between emoji and text - reduced by 50%
        width: "16.875rem", // 270px exact width
        height: "3rem", // 48px
        backgroundColor: badgeConfig.backgroundColor,
        paddingLeft: "0.875rem", // 14px - reduced for better left/right balance
        paddingRight: "1.375rem", // 22px - increased to create more right space
        paddingTop: "0.6875rem", // 11px
        paddingBottom: "0.6875rem", // 11px
        borderRadius: "1.375rem", // 22px border radius
        cursor: onClick ? "pointer" : "default",
        transition: "all 200ms ease",
        fontFamily: "Montserrat, sans-serif", // Montserrat instead of Inter
        fontWeight: 600, // Semibold
        fontSize: "1.375rem", // 22px for logo text
        color: badgeConfig.color,
        whiteSpace: "nowrap",
        ...style,
      };
    }

    // Early Access compact styling
    return {
      width: "12.5rem", // 200px converted to rem
      height: "3rem", // 48px converted to rem
      backgroundColor: badgeConfig.backgroundColor,
      borderRadius: "1.375rem", // 22px converted to rem
      padding: "0.6875rem 1.375rem", // py-11px px-22px converted to rem
      display: "flex",
      alignItems: "center",
      justifyContent: "center", // Center content
      gap: "0.25rem", // Reduced by 50% from 0.5rem to 0.25rem
      cursor: onClick ? "pointer" : "default",
      transition: "all 200ms ease",
      fontFamily: "Inter, sans-serif",
      fontWeight: 600, // Semibold
      fontSize: "1.25rem", // 20px converted to rem
      color: badgeConfig.color,
      whiteSpace: "nowrap", // Prevent text wrapping
      overflow: "hidden", // Hide overflow
      textOverflow: "ellipsis", // Add ellipsis if text is too long
      boxSizing: "border-box", // Include padding in width calculation
      ...style,
    };
  };

  const getMobileStyles = () => {
    const isLogo = badgeConfig.type === "logo";

    if (isLogo) {
      return {
        display: "flex",
        alignItems: "center",
        justifyContent: "center", // Center content within mobile badge
        gap: "0.125rem", // Gap between emoji and text - reduced by 50%
        width: "9rem", // 144px exact mobile width
        height: "1.625rem", // 26px mobile height (24px + 2px)
        backgroundColor: badgeConfig.backgroundColor,
        paddingLeft: "0.5rem", // 8px - reduced for better left/right balance
        paddingRight: "0.75rem", // 12px - increased to create more right space
        paddingTop: "0.375rem", // 6px
        paddingBottom: "0.375rem", // 6px
        borderRadius: "0.75rem", // 12px mobile border radius
        cursor: onClick ? "pointer" : "default",
        transition: "all 200ms ease",
        fontFamily: "Montserrat, sans-serif", // Montserrat for mobile logo
        fontWeight: 600, // Semibold
        fontSize: "0.75rem", // 12px for mobile logo text
        color: badgeConfig.color,
        whiteSpace: "nowrap",
        margin: "0 !important",
        border: "none !important",
        ...style,
      };
    }

    // Early Access compact mobile styling
    return {
      width: "7.5rem", // 120px converted to rem - increased by 8px for better balance
      height: "1.5rem", // 24px converted to rem
      backgroundColor: badgeConfig.backgroundColor,
      borderRadius: "0.75rem", // 12px converted to rem
      padding: "0.5rem 1.375rem !important", // py-8px px-22px - force override any conflicting styles
      display: "flex",
      alignItems: "center",
      justifyContent: "center", // Center content
      gap: "0.125rem", // Small gap for mobile
      cursor: onClick ? "pointer" : "default",
      transition: "all 200ms ease",
      fontFamily: "Inter, sans-serif",
      fontWeight: 600, // Semibold
      fontSize: "0.75rem", // 12px converted to rem
      color: badgeConfig.color,
      whiteSpace: "nowrap", // Prevent text wrapping
      overflow: "hidden", // Hide overflow if text is too long
      boxSizing: "border-box", // Include padding in width calculation
      margin: "0 !important", // Reset any inherited margin
      border: "none !important", // Reset any inherited border
      ...style,
    };
  };

  const badgeStyle = isMobile ? getMobileStyles() : getDesktopTabletStyles();

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  const handleMouseEnter = (e) => {
    if (onClick) {
      e.target.style.opacity = "0.8";
    }
  };

  const handleMouseLeave = (e) => {
    if (onClick) {
      e.target.style.opacity = "1";
    }
  };

  return (
    <div
      style={badgeStyle}
      className={className}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {(customIcon || badgeConfig.icon) && (
        <span
          style={{
            fontSize:
              badgeConfig.type === "logo"
                ? isMobile
                  ? "1rem"
                  : "1.875rem" // Logo: Mobile 16px, Desktop 30px
                : isMobile
                  ? "1rem"
                  : "1.5rem", // Early Access: Mobile 16px, Desktop 24px
          }}
        >
          {customIcon || badgeConfig.icon}
        </span>
      )}
      <span>{customText || badgeConfig.text}</span>
    </div>
  );
};

export default Badge;
